/**
 * TextArea Component
 * 
 * Multi-line text input with:
 * - Auto-resize capability
 * - Character count
 * - Error state styling
 * - Animated feedback
 * 
 * Optimized for longer text responses in the funnel.
 */

import { forwardRef, TextareaHTMLAttributes, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// TYPES
// ============================================

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text for the textarea */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Helper text shown below textarea */
  helperText?: string;
  /** Show character count */
  showCharCount?: boolean;
  /** Maximum character limit */
  maxLength?: number;
  /** Minimum character requirement (for visual feedback) */
  minLength?: number;
  /** Enable auto-resize based on content */
  autoResize?: boolean;
  /** Full width of container */
  fullWidth?: boolean;
}

// ============================================
// COMPONENT
// ============================================

/**
 * TextArea component with label, error states, and auto-resize
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      helperText,
      showCharCount = false,
      maxLength,
      minLength,
      autoResize = true,
      fullWidth = true,
      className = '',
      id,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    
    // Internal ref for auto-resize
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    
    // Track focus state for styling
    const [isFocused, setIsFocused] = useState(false);
    
    // Track character count
    const charCount = typeof value === 'string' ? value.length : 0;

    // Combine refs
    const setRefs = (element: HTMLTextAreaElement | null) => {
      internalRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    // Auto-resize effect
    useEffect(() => {
      if (autoResize && internalRef.current) {
        // Reset height to auto to get the correct scrollHeight
        internalRef.current.style.height = 'auto';
        // Set height to scrollHeight
        internalRef.current.style.height = `${internalRef.current.scrollHeight}px`;
      }
    }, [value, autoResize]);

    // Determine if min length is met (for visual feedback)
    const isMinLengthMet = minLength ? charCount >= minLength : true;

    // Build textarea class string
    const textareaClasses = `
      w-full min-h-[120px] p-4
      rounded-lg border-2
      text-base text-text-primary leading-relaxed
      placeholder:text-text-secondary/60
      resize-none
      transition-all duration-200
      focus:outline-none
      ${error 
        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20' 
        : 'border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
      }
      disabled:bg-surface disabled:cursor-not-allowed
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textareaId}
            className={`
              block mb-2 text-sm font-medium
              transition-colors duration-200
              ${isFocused ? 'text-primary' : 'text-text-primary'}
              ${error ? 'text-red-500' : ''}
            `}
          >
            {label}
            {props.required && (
              <span className="text-red-500 ml-1" aria-hidden="true">*</span>
            )}
          </label>
        )}

        {/* Textarea field */}
        <textarea
          ref={setRefs}
          id={textareaId}
          className={textareaClasses}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${textareaId}-error` : helperText ? `${textareaId}-helper` : undefined
          }
          {...props}
        />

        {/* Bottom row: error/helper text and character count */}
        <div className="mt-2 flex justify-between items-start gap-4">
          {/* Error or helper text */}
          <AnimatePresence mode="wait">
            {error ? (
              <motion.p
                key="error"
                id={`${textareaId}-error`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-sm text-red-500 flex items-center gap-1"
                role="alert"
              >
                {/* Error icon */}
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </motion.p>
            ) : helperText ? (
              <motion.p
                key="helper"
                id={`${textareaId}-helper`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-text-secondary"
              >
                {helperText}
              </motion.p>
            ) : (
              <div /> // Spacer
            )}
          </AnimatePresence>

          {/* Character count */}
          {showCharCount && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`
                text-sm flex-shrink-0
                ${!isMinLengthMet ? 'text-amber-500' : 'text-text-secondary'}
                ${error ? 'text-red-500' : ''}
              `}
            >
              {charCount}
              {minLength && !isMinLengthMet && ` / ${minLength} min`}
              {maxLength && ` / ${maxLength}`}
            </motion.span>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
